<script setup lang="ts">
import { ref, watch } from 'vue'
import Button from '@/components/Button.vue'
import Message from '@/components/Message.vue'
import * as OtpService from '@/services/otp'

const id = ref('')
const otp = ref('')
const error = ref('')
const state = ref<
  'idle' | 'loading' | 'otp_input' | 'otp_loading' | 'error' | 'exited'
>('idle')

const handleSubmit = async () => {
  if (id.value.trim().length < 3) {
    error.value = 'Please enter a valid email or mobile number'
    return
  }
  state.value = 'loading'
  try {
    await OtpService.sendOtp(id.value)
    state.value = 'otp_input'
  } catch (e) {
    error.value = 'Failed to send one time login code'
    state.value = 'error'
  }
}

const handleOtpSubmit = async () => {
  state.value = 'otp_loading'
  try {
    const res = await OtpService.verifyOtp(id.value, otp.value)
    if (res.token) {
      if (window.mixpanel) {
        window.mixpanel.track('passwordless_login_success', {
          identifier: id.value,
        })
      }

      window.location.href = `https://account.bowercollective.com/login?token=${res.token}`
    } else {
      if (window.mixpanel) {
        window.mixpanel.track('passwordless_login_failure', {
          identifier: id.value,
        })
      }
      error.value = 'Invalid login code'
      state.value = 'error'
    }
  } catch (e) {
    if (window.mixpanel) {
      window.mixpanel.track('passwordless_login_failure', {
        identifier: id.value,
      })
    }
    error.value = 'Failed to verify one time login code'
    state.value = 'error'
  }
}

const exit = (e: Event) => {
  e.preventDefault()
  state.value = 'exited'
}

watch(
  state,
  (newState) => {
    const elementsToShow = document.querySelector('#legacy-login-root')
    if (newState === 'exited') {
      if (elementsToShow) {
        ;(elementsToShow as HTMLElement).style.display = 'block'
      }
    } else if (newState === 'idle') {
      if (elementsToShow) {
        ;(elementsToShow as HTMLElement).style.display = 'none'
      }
    }
  },
  {
    immediate: true,
  },
)
</script>
<template>
  <div v-if="state !== 'exited'">
    <form
      @submit.prevent="handleSubmit"
      class="LoginApp Stack Stack--tiny"
      v-if="state === 'idle' || state === 'loading'"
    >
      <div>
        <label for="id">Email or mobile number</label>
        <input type="text" v-model="id" id="id" autofocus />
      </div>
      <div v-if="error">
        <Message mode="error">
          <template #content>
            {{ error }}
          </template>
        </Message>
      </div>
      <div>
        <Button
          class="Button Button--peach Button--squared Button--block"
          type="submit"
          :disabled="state === 'loading'"
          :loading="state === 'loading'"
        >
          Get your one time login code
        </Button>
      </div>
    </form>
    <form
      @submit.prevent="handleOtpSubmit"
      class="LoginApp Stack Stack--tiny"
      v-else-if="
        state === 'otp_input' || state === 'otp_loading' || state === 'error'
      "
    >
      <div>
        <Message mode="mid">
          <template #content>
            <p>
              <strong
                >We’ve sent you a one time login code via email and/or
                SMS.</strong
              >
            </p>
            <p>
              Please enter it below to login. If you don’t receive it, make sure
              you used the same email or phone number that you used with us
              before.
            </p>
          </template>
        </Message>
      </div>
      <div>
        <label for="otp">Enter your login code</label>
        <input type="text" v-model="otp" id="otp" autofocus />
      </div>
      <div>
        <Message mode="error" v-if="error">
          <template #content>
            {{ error }}
          </template>
        </Message>
      </div>
      <div class="ButtonGroup">
        <Button
          class="Button Button--peach Button--squared Button--block"
          type="submit"
          :disabled="state === 'otp_loading'"
          :loading="state === 'otp_loading'"
        >
          Login
        </Button>
        <Button
          class="Button--link Button--block"
          @click="(state = 'idle'), (error = '')"
          >Go back</Button
        >
      </div>
    </form>
    <a href="#" class="LoginApp__UtilLink" @click="exit"
      >Use email and password instead</a
    >
  </div>
  <div v-else>
    <Button
      class="Button--peach Button--squared Button--block LoginApp__SwitchButton"
      @click="state = 'idle'"
    >
      Switch to passwordless login
    </Button>
    <div class="Divider LoginApp__Divider" />
  </div>
</template>

<style>
.LoginApp {
  max-width: 600px;
  margin: 0 auto 120px;

  input {
    width: 100%;
  }

  .Message {
    display: block;
  }
}

.LoginApp__UtilLink {
  display: block;
  text-align: center;
  margin: 20px 0;
}

.LoginApp__SwitchButton {
  margin: 20px 0;
}

.LoginApp__Divider {
  margin: 20px 0;
}
</style>
